import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { useHistory } from "react-router-dom"
import auth from "./api/authSlice"
import pageSlice from "./api/pageSlice"
import Loader from "./component/Loader"
import LicenseAgreementDialog from "./component/dialog/LicenseAgreementDialog"
import licenseSlice from "./pages/admin/licensing/licenseSlice"

const { actions: pageActions } = pageSlice

/**
 * The component responsible for checking the user session and license agreement.
 *
 * If the cookie is still alive and the user is asking for "private" pages, the user is redirected to the log in page.
 *
 * @param children
 * @returns {JSX.Element|*}
 * @constructor
 */
export default function SessionCheck({ children }) {
  const dispatch = useDispatch()
  const checking = useSelector((state) => state.auth.sessionChecking)
  const userLocale = useSelector((state) => state.intl.locale)
  const history = useHistory()
  const licenseConfiguration = useSelector(
    (state) => state.license.configuration
  )
  const licenseDialogIsShowing = useSelector(
    (state) => state.license.dialogIsShowing
  )
  const licenseDialogIsPreview = useSelector(
    (state) => state.license.dialogIsPreview
  )

  useEffect(() => {
    // Check if current session is available locally in browser side
    dispatch(auth.actions.checkingSession())
    // Load user pages
    dispatch(pageActions.fetchPagesInfos())
  }, [dispatch])

  if (checking) {
    return <Loader />
  }

  function onLicenseAccept() {
    dispatch(licenseSlice.actions.acceptLicense())
  }

  function onLicenseReject() {
    dispatch(licenseSlice.actions.rejectLicense())
    dispatch(auth.actions.logout()).then(() => history.push("/login"))
  }

  function onDialogClose() {
    if (!licenseDialogIsPreview) {
      // Disallow any other exit if acceptance or rejection is mandatory
      return
    }

    dispatch(
      licenseSlice.actions.setLicenseDialogIsShowing({ isShowing: false })
    )
  }

  // The license configuration is null when not fetched (such as when the user is a staff)
  const licenseText = licenseConfiguration?.locales?.[userLocale]

  return (
    <>
      <LicenseAgreementDialog
        isPreview={licenseDialogIsPreview}
        onAccept={onLicenseAccept}
        onReject={onLicenseReject}
        open={licenseDialogIsShowing}
        licenseText={licenseText}
        onClose={onDialogClose}
      />
      {children}
    </>
  )
}

SessionCheck.propTypes = {
  children: PropTypes.any.isRequired
}
